import { CartItem, DeliveryRules, ProductDeliveryRules, ProductDeliveryShippingFee, ProductModel } from "../types/models/mvcapi";
import { reduce } from "lodash";
import { UUID } from "../types/basetypes";
import { findKeyWithHighestValue } from "./util-logic";
import { EMPTY_UUID } from "./util";


/**
 * This is used to understand how many kinds of shipping rate chart we have in a shopping cart.
 * You can see this a a hash of the shipping rate table for a product
 */        
export function getShippingFeesSignature(availableFees: ProductDeliveryShippingFee[]) {
    return availableFees ? availableFees.map(f => f.MethodId).sort().join("_") : "";
}

export type CartDeliveryStatus = {
    itemCount: number;
    unsetItemCount: number;
    boxOfficeItemCount: number;
    mailItemCount: number;
    eTicketItemCount: number;
    isBoxOfficeAvailable: boolean;
    isMailDeliveryAvailable: boolean;
    isEticketDeliveryAvailable: boolean;
    shippingFeesSignatures: string[];
    shippingFees: ProductDeliveryShippingFee[];
    shippingFeesCount: {
        [key: string]: number;
    },
    mostUsedShippingFeeID: UUID;
}

export function getDeliveryStatus(acc: CartDeliveryStatus, method: DeliveryMethod, isMailDeliveryAvailable: boolean, isETicketDeliveryAvailable: boolean, isDeliveryMandatory: boolean, shippingMethodFees: ProductDeliveryShippingFee[], shippingFeeId: UUID): CartDeliveryStatus {
    switch(method) {
        case DeliveryMethod.NONE: acc.unsetItemCount++; break;
        case DeliveryMethod.MAIL: acc.mailItemCount++; break;
        case DeliveryMethod.ETICKET: acc.eTicketItemCount++; break;
        case DeliveryMethod.BOX_OFFICE: acc.boxOfficeItemCount++; break;
    }

    if(!isDeliveryMandatory) acc.isBoxOfficeAvailable = true;
    if(isMailDeliveryAvailable) acc.isMailDeliveryAvailable = true;
    if(isETicketDeliveryAvailable) acc.isEticketDeliveryAvailable = true;

    if(method === DeliveryMethod.MAIL && shippingFeeId !== EMPTY_UUID) {
        if(!acc.shippingFeesCount[shippingFeeId]) acc.shippingFeesCount[shippingFeeId] = 0;
        acc.shippingFeesCount[shippingFeeId]++;
    }

    if(isMailDeliveryAvailable) {
        const signature = getShippingFeesSignature(shippingMethodFees);
        if(!acc.shippingFeesSignatures.includes(signature)) acc.shippingFeesSignatures.push(signature);

        // Add it to the list if not already added
        shippingMethodFees.forEach((sf) => {
            if(!acc.shippingFees.some((x) => x.MethodId === sf.MethodId))
                acc.shippingFees.push(sf);
        });
    }

    return acc;
}
export function getWholeCartDeliveryStatus(cartItems: CartItem[]): CartDeliveryStatus{
    // Est-ce qu'il n'y a aucun shipping de selectionné?
    let result = reduce(
        cartItems,
        function(acc: CartDeliveryStatus, item) {
            acc.itemCount++;
            const method = getDeliveryMethodFromCartItem(item);
            acc = getDeliveryStatus(acc, method, item.DeliveryRules.IsMailDeliveryAvailable, item.DeliveryRules.IsETicketDeliveryAvailable, item.DeliveryRules.IsDeliveryMandatory, item.DeliveryRules.ShippingMethodFees, item.ShippingFeeID);
            return acc;
        },
        {
            itemCount: 0,
            unsetItemCount: 0,
            boxOfficeItemCount: 0,
            mailItemCount: 0,
            eTicketItemCount: 0,
            isBoxOfficeAvailable: false,
            isMailDeliveryAvailable: false,
            isEticketDeliveryAvailable: false,
            shippingFeesSignatures: [],
            shippingFees: [],
            shippingFeesCount: {},
            mostUsedShippingFeeID: null
        }
    );
    
    result.mostUsedShippingFeeID = findKeyWithHighestValue(result.shippingFeesCount);

    return result;
}

export function getCartDeliveryStatusFromRules(deliveryRulesByProduct: ProductDeliveryRules, products: ProductModel[]): CartDeliveryStatus {
    let result = reduce(
        products,
        function(acc: CartDeliveryStatus, product) {
            acc.itemCount++;
            const productId = product.IsPackageProduct ? product.PackageID : product.EventID;
            const curDeliveryRules = deliveryRulesByProduct[productId];
            const method = getDeliveryMethodFromRule(curDeliveryRules);
            acc = getDeliveryStatus(acc, method, curDeliveryRules.IsMailDeliveryAvailable, curDeliveryRules.IsETicketDeliveryAvailable, curDeliveryRules.IsDeliveryMandatory, curDeliveryRules.ShippingMethodFees, product.ShippingFeeId);
            return acc;
        },
        {
            itemCount: 0,
            unsetItemCount: 0,
            boxOfficeItemCount: 0,
            mailItemCount: 0,
            eTicketItemCount: 0,
            isBoxOfficeAvailable: false,
            isMailDeliveryAvailable: false,
            isEticketDeliveryAvailable: false,
            shippingFeesSignatures: [],
            shippingFees: [],
            shippingFeesCount: {},
            mostUsedShippingFeeID: null
        }
    );

    result.mostUsedShippingFeeID = findKeyWithHighestValue(result.shippingFeesCount);
    return result;
}

export function getDeliveryMethodFromRule(deliveryRule: DeliveryRules): DeliveryMethod {
    if(deliveryRule.IsETicketDeliveryAvailable) return DeliveryMethod.ETICKET;
    else if(deliveryRule.IsMailDeliveryAvailable) return DeliveryMethod.MAIL;
    else if(deliveryRule.IsDeliveryMandatory) return DeliveryMethod.BOX_OFFICE;
    else return DeliveryMethod.NONE;
}


export function getSelectedDeliveryFromDeliveryStatus(cartDeliveryStatus: CartDeliveryStatus) {
    if(cartDeliveryStatus.eTicketItemCount > 0) return DeliveryMethod.ETICKET;
    else if(cartDeliveryStatus.mailItemCount > 0) return DeliveryMethod.MAIL;
    else if(cartDeliveryStatus.boxOfficeItemCount > 0) return DeliveryMethod.BOX_OFFICE;
    else return DeliveryMethod.NONE;
}

// Delivery helper functions
export enum DeliveryMethod {
    BOX_OFFICE = "BOX_OFFICE",
    MAIL = "MAIL",
    ETICKET = "ETICKET",
    NONE = "NONE"
}

export function getDeliveryMethodFromCartItem(cartItem: CartItem){
    if(!cartItem.DeliveryMethodSet)
        return DeliveryMethod.NONE;
    else if(cartItem.IsMailDelivery)
        return DeliveryMethod.MAIL;
    else if(cartItem.IsEticketDelivery)
        return DeliveryMethod.ETICKET;
    else
        return DeliveryMethod.BOX_OFFICE;
}